import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { useSiteData, useSectionDesign } from "../../context/SiteData";
import { useBooking } from "../../context/BookingContext";
import { optImg } from "../../lib/img";

const TourGallery = ({ images, alt, testid }) => {
  const trackRef = useRef(null);
  const [idx, setIdx] = useState(0);

  const go = (i) => {
    const track = trackRef.current;
    if (!track) return;
    const next = (i + images.length) % images.length;
    track.scrollTo({ left: next * track.clientWidth, behavior: "smooth" });
    setIdx(next);
  };

  const onScroll = () => {
    const track = trackRef.current;
    if (track) setIdx(Math.round(track.scrollLeft / track.clientWidth));
  };

  return (
    <div className="relative h-[var(--tour-img-h)] group/gal" data-testid={testid}>
      <div
        ref={trackRef}
        onScroll={onScroll}
        className="flex h-full overflow-x-auto snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {images.map((src, i) => (
          <img
            key={i}
            src={optImg(src, 800)}
            alt={`${alt} — photo ${i + 1}`}
            loading="lazy" decoding="async"
            draggable={false}
            className="w-full h-full object-cover shrink-0 snap-center"
          />
        ))}
      </div>
      {images.length > 1 && (
        <>
          <button
            data-testid={`${testid}-prev`}
            onClick={(e) => {
              e.stopPropagation();
              go(idx - 1);
            }}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 backdrop-blur flex items-center justify-center text-[#0b132b] shadow-md opacity-0 group-hover/gal:opacity-100 transition-opacity duration-300 hover:bg-white"
            aria-label="Previous photo"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            data-testid={`${testid}-next`}
            onClick={(e) => {
              e.stopPropagation();
              go(idx + 1);
            }}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 backdrop-blur flex items-center justify-center text-[#0b132b] shadow-md opacity-0 group-hover/gal:opacity-100 transition-opacity duration-300 hover:bg-white"
            aria-label="Next photo"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 flex gap-1.5" data-testid={`${testid}-dots`}>
            {images.map((_, i) => (
              <span
                key={i}
                className={`w-1.5 h-1.5 rounded-full transition-colors duration-300 ${i === idx ? "bg-white" : "bg-white/50"}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export const Tours = () => {
  const { tours, content, fmtPrice } = useSiteData();
  const sec = useSectionDesign("tours");
  const { openBooking } = useBooking();

  return (
    <section id="tours" className="relative py-24 sm:py-32 bg-[#f8fafc]" data-testid="tours-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="max-w-2xl">
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-wider text-[var(--brand)]" data-testid="tours-eyebrow">
            {content.tours_eyebrow}
          </p>
          <h2 className="mt-4 font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#0b132b]" data-testid="tours-title">
            {sec.text("headline", content.tours_title)}
          </h2>
          <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">
            {sec.text("subtext", content.tours_subtitle)}
          </p>
        </Reveal>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="tours-grid">
          {tours.map((tour, i) => (
            <Reveal key={tour.id} delay={(i % 3) * 0.1}>
              <motion.article
                data-testid={`tour-card-${tour.id}`}
                whileHover={{ y: -8 }}
                transition={{ type: "spring", stiffness: 260, damping: 22 }}
                className="group h-full rounded-3xl bg-white border border-slate-100 overflow-hidden shadow-[0_16px_40px_-20px_rgba(11,19,43,0.15)] hover:shadow-[0_30px_60px_-24px_rgba(225,29,72,0.35)] hover:border-[var(--brand)]/25 transition-colors duration-300 flex flex-col"
              >
                <div className="relative overflow-hidden">
                  <Link
                    to={`/tours/${tour.slug || tour.id}`}
                    data-testid={`tour-card-link-${tour.id}`}
                    aria-label={`View details of ${tour.name}`}
                  >
                    <TourGallery
                      images={[tour.image, ...(tour.images || [])]}
                      alt={`${tour.name} — ${tour.destination}, Switzerland`}
                      testid={`tour-gallery-${tour.id}`}
                    />
                  </Link>
                  <span className="absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 rounded-full bg-white/90 backdrop-blur px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide text-[var(--brand)] pointer-events-none">
                    <Clock className="w-3.5 h-3.5" /> {tour.duration}
                  </span>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{tour.destination}</p>
                  <h3 className="mt-1.5 font-display font-bold text-xl text-[#0b132b] leading-tight">
                    <Link
                      to={`/tours/${tour.slug || tour.id}`}
                      data-testid={`tour-card-title-link-${tour.id}`}
                      className="hover:text-[var(--brand)] transition-colors"
                    >
                      {tour.name}
                    </Link>
                  </h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{tour.description}</p>
                  <Link
                    to={`/tours/${tour.slug || tour.id}`}
                    data-testid={`tour-card-details-link-${tour.id}`}
                    className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-[var(--brand)] hover:gap-2.5 transition-all flex-1 items-end"
                  >
                    View details & itinerary <ArrowRight className="w-4 h-4" />
                  </Link>
                  <div className="mt-5 flex items-center justify-between gap-3">
                    <p className="leading-none">
                      <span className="text-[11px] font-semibold text-slate-400 block mb-1">from</span>
                      {tour.sale_price ? (
                        <>
                          <span className="font-display font-extrabold text-xl text-[var(--brand)]">{fmtPrice(tour.sale_price)}</span>
                          <span className="ml-1.5 text-xs font-semibold text-slate-400 line-through">{fmtPrice(tour.price)}</span>
                        </>
                      ) : (
                        <span className="font-display font-extrabold text-xl text-[var(--brand)]">{fmtPrice(tour.price)}</span>
                      )}
                      
                    </p>
                    <button
                      data-testid={`tour-card-book-button-${tour.id}`}
                      onClick={() => openBooking({ mode: "tour", tour })}
                      className="group/btn inline-flex items-center gap-1.5 rounded-full bg-[var(--brand)] text-white text-sm font-bold px-5 py-2.5 shadow-lg shadow-[var(--brand)]/30 transition-all duration-300 hover:bg-[var(--brand-dark)]"
                    >
                      Book Now
                      <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover/btn:translate-x-1" />
                    </button>
                  </div>
                </div>
              </motion.article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
